import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutlets } from '@/hooks/useOutlets';
import { OutletStatusBadge } from '@/components/outlets/OutletStatusBadge';
import { OutletDeviceStatusGrid } from '@/components/outlets/OutletDeviceStatusGrid';
import { connectWebSocket, getSocket } from '@/lib/websocket';
import { formatDistanceToNow } from 'date-fns';
import type { OutletStatusInfo } from '@photobooth/types';

export function OutletsPage() {
  const navigate = useNavigate();
  const { outlets, statuses, isLoading, error, updateOutletStatus } = useOutlets();
  const [wsConnected, setWsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOutlets = searchQuery.trim()
    ? outlets.filter(
      (o) =>
        o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
    )
    : outlets;

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    const socket = connectWebSocket();

    socket.on('connect', () => setWsConnected(true));
    socket.on('disconnect', () => setWsConnected(false));

    socket.on('outlet:status', (status: OutletStatusInfo) => {
      updateOutletStatus(status);
    });

    // Subscribe to all outlets
    socket.emit('admin:subscribe', outlets.map(o => o.outletId));

    return () => {
      const currentSocket = getSocket();
      if (currentSocket) {
        currentSocket.off('outlet:status');
      }
    };
  }, [outlets]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Outlets</h1>
          <p className="text-text-muted mt-1">
            Manage photo booth outlets and monitor their status
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
          <input
            type="search"
            placeholder="Search outlets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-64"
          />
          <div className="flex items-center justify-between md:justify-start gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-text-muted">
                {wsConnected ? 'Live updates' : 'Disconnected'}
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate('/outlets/new')}
            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            + New Outlet
          </button>
        </div>
      </div>

      {outlets.length === 0 ? (
        <div className="bg-surface rounded-lg shadow p-12 text-center">
          <p className="text-text-muted mb-4">No outlets found</p>
          <button
            onClick={() => navigate('/outlets/new')}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Create Your First Outlet
          </button>
        </div>
      ) : (
        <>
          {searchQuery && (
            <p className="text-sm text-text-muted mb-4">
              Showing {filteredOutlets.length} of {outlets.length} outlets
            </p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredOutlets.map((outlet) => {
              const rawStatus = statuses.get(outlet.outletId);

              const status = rawStatus
                ? (() => {
                  const counts = rawStatus.deviceCounts ?? {
                    capture: 0,
                    customer: 0,
                    cashier: 0,
                  };
                  const total =
                    counts.capture + counts.customer + counts.cashier;
                  const allRolesOnline =
                    counts.capture > 0 &&
                    counts.customer > 0 &&
                    counts.cashier > 0;

                  let derivedStatus: OutletStatusInfo['status'];
                  if (total === 0) {
                    derivedStatus = 'offline';
                  } else if (allRolesOnline) {
                    derivedStatus = 'online';
                  } else {
                    derivedStatus = 'degraded';
                  }

                  return {
                    ...rawStatus,
                    status: derivedStatus,
                    deviceCounts: counts,
                  };
                })()
                : undefined;

              return (
                <div
                  key={outlet.outletId}
                  className="bg-surface rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/outlets/${outlet.outletId}`)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-text">{outlet.name}</h3>
                        <p className="text-text-muted text-sm">{outlet.location}</p>
                      </div>
                      {status && <OutletStatusBadge status={status.status} />}
                    </div>

                    {status && (
                      <div className="space-y-3">
                        <OutletDeviceStatusGrid deviceCounts={status.deviceCounts} />

                        <div className="text-xs text-text-muted">
                          Last heartbeat: {formatDistanceToNow(new Date(status.lastHeartbeat), { addSuffix: true })}
                        </div>
                      </div>
                    )}

                    {!status && (
                      <div className="text-sm text-text-muted">
                        No status information available
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {searchQuery && filteredOutlets.length === 0 && (
            <p className="text-center text-text-muted py-8">No outlets match your search</p>
          )}
        </>
      )}
    </div>
  );
}
